import React, {useEffect, useMemo, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {keyframes} from "@emotion/react";
import {
    AspectRatio,
    Box,
    Button,
    Container,
    Flex,
    Heading,
    HStack,
    IconButton,
    Image,
    Link,
    SimpleGrid,
    Stack,
    Text,
    VisuallyHidden,
    VStack,
} from "@chakra-ui/react";
import {ArrowForwardIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, EmailIcon, PhoneIcon} from "@chakra-ui/icons";
import {catalogPhotos, categories, imageUrl, subCategoryUrl} from "../data/catalog";
import {featuredPhotos, heroSlides, reasons, services, site, steps} from "../data/site";
import processBg from "../gallery/cover_1.jpg";
import SectionHeading from "../components/SectionHeading";
import CategoryTile from "../components/CategoryTile";
import QuoteForm from "../components/QuoteForm";
import Lightbox from "../components/Lightbox";
import {useQuote} from "../components/QuoteContext";
import usePageTitle from "../usePageTitle";

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
`;

function Hero() {
    const {openQuote} = useQuote();
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const count = heroSlides.length;

    useEffect(() => {
        if (paused) return undefined;
        const timer = setTimeout(() => setActive((i) => (i + 1) % count), 6000);
        return () => clearTimeout(timer);
    }, [active, paused, count]);

    const slide = heroSlides[active];
    const arrowStyle = {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        variant: "ghost",
        color: "white",
        fontSize: "3xl",
        size: "lg",
        _hover: {bg: "whiteAlpha.200"},
        display: {base: "none", lg: "inline-flex"},
        zIndex: 1,
    };

    return (
        <Box as="section" position="relative" h={{base: "540px", md: "620px", xl: "700px"}} overflow="hidden" bg="ink.900"
             onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            {heroSlides.map((s, i) => (
                <Box key={s.title} position="absolute" inset={0} bgImage={`url(${s.image})`} bgSize="cover" bgPosition={s.position}
                     opacity={i === active ? 1 : 0} transform={i === active ? "scale(1.05)" : "scale(1)"}
                     transition="opacity 1.2s ease, transform 7s linear" aria-hidden={i !== active}/>
            ))}
            <Box position="absolute" inset={0}
                 bgImage="linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.38) 55%, rgba(0,0,0,0.12) 100%)"/>

            <Container maxW="container.xl" position="relative" h="100%" display="flex" alignItems="center" px={{base: 5, lg: 20}}>
                <Box maxW="640px" color="white" key={active} animation={`${fadeUp} 0.8s ease both`}>
                    <VisuallyHidden as="h1">{site.name}: custom doors, windows and joinery</VisuallyHidden>
                    <Text textTransform="uppercase" letterSpacing="0.22em" fontSize="xs" fontWeight={700} color="brand.200">
                        {site.name}
                    </Text>
                    <Heading as="p" color="white" fontSize={{base: "4xl", md: "6xl"}} lineHeight={1.05} textTransform="uppercase"
                             letterSpacing="0.01em" mt={4}>
                        {slide.title}
                    </Heading>
                    <Text mt={5} fontSize={{base: "md", md: "xl"}} color="whiteAlpha.900" maxW="520px">
                        {slide.description}
                    </Text>
                    <Stack direction={{base: "column", sm: "row"}} spacing={3} mt={8}>
                        <Button size="lg" onClick={() => openQuote()}>Book a free quote</Button>
                        <Button as={RouterLink} to="/#products" size="lg" variant="outline" colorScheme="whiteAlpha" color="white"
                                borderColor="whiteAlpha.700" _hover={{bg: "whiteAlpha.200"}}>
                            View products
                        </Button>
                    </Stack>
                </Box>
            </Container>

            <IconButton aria-label="Previous slide" icon={<ChevronLeftIcon/>} left={4}
                        onClick={() => setActive((active - 1 + count) % count)} {...arrowStyle}/>
            <IconButton aria-label="Next slide" icon={<ChevronRightIcon/>} right={4}
                        onClick={() => setActive((active + 1) % count)} {...arrowStyle}/>

            <HStack position="absolute" bottom={{base: 16, md: 20}} left={0} right={0} justify="center" spacing={2}>
                {heroSlides.map((s, i) => (
                    <Box key={s.title} as="button" type="button" aria-label={`Show slide ${i + 1}`} onClick={() => setActive(i)}
                         w={i === active ? "36px" : "18px"} h="3px" bg={i === active ? "white" : "whiteAlpha.500"}
                         transition="all 0.3s" py={0}/>
                ))}
            </HStack>
        </Box>
    );
}

function Reasons() {
    return (
        <Container maxW="container.xl" position="relative" zIndex={1} mt={{base: -10, md: -14}}>
            <SimpleGrid columns={{base: 2, md: 4}} bg="white" boxShadow="0 20px 50px rgba(0,0,0,0.12)">
                {reasons.map((reason, i) => (
                    <Box key={reason.label} textAlign="center" py={{base: 6, md: 9}} px={4} borderStyle="solid" borderColor="ink.100"
                         borderLeftWidth={{base: i % 2 ? "1px" : 0, md: i ? "1px" : 0}}
                         borderTopWidth={{base: i > 1 ? "1px" : 0, md: 0}}>
                        <Text fontFamily="heading" fontWeight={700} fontSize={{base: "3xl", md: "4xl"}} color="brand.500"
                              lineHeight={1} textTransform="uppercase">
                            {reason.value}
                        </Text>
                        <Text mt={2} fontSize="xs" fontWeight={600} letterSpacing="0.14em" textTransform="uppercase" color="ink.500">
                            {reason.label}
                        </Text>
                    </Box>
                ))}
            </SimpleGrid>
        </Container>
    );
}

function Products() {
    const {openQuote} = useQuote();
    return (
        <Box as="section" id="products" py={{base: 16, md: 24}}>
            <Container maxW="container.xl">
                <SectionHeading eyebrow="Custom made" title="Our products">
                    Doors, windows, stair cases and timber work: custom made, supplied and professionally installed.
                </SectionHeading>
                <SimpleGrid columns={{base: 1, sm: 2, lg: 4}} spacing={4}>
                    {categories.map((category) => (
                        <CategoryTile key={category.path} category={category}/>
                    ))}
                    <Flex direction="column" justify="flex-end" h={{base: "240px", md: "320px"}} p={{base: 5, md: 6}}
                          bg="brand.500" color="white">
                        <Text fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="whiteAlpha.800">
                            Something else?
                        </Text>
                        <Heading as="h3" color="white" fontSize={{base: "lg", md: "xl"}} textTransform="uppercase"
                                 letterSpacing="0.03em" lineHeight={1.2} mt={1}>
                            Custom carpentry & joinery
                        </Heading>
                        <Text mt={3} fontSize="sm" color="whiteAlpha.900">
                            Tell us what you need and we will measure and quote it for free.
                        </Text>
                        <Button mt={5} alignSelf="flex-start" bg="white" color="brand.600" _hover={{bg: "ink.50"}}
                                onClick={() => openQuote("Other carpentry / joinery")}>
                            Get a free quote
                        </Button>
                    </Flex>
                </SimpleGrid>
            </Container>
        </Box>
    );
}

function About() {
    const points = [
        `Licence No. ${site.license}`,
        `ABN ${site.abn}`,
        "Free estimate & quote",
        "Supply, installation & custom made work",
    ];
    return (
        <Box as="section" id="about" py={{base: 16, md: 24}} bg="ink.50">
            <Container maxW="container.xl">
                <SimpleGrid columns={{base: 1, lg: 2}} spacing={{base: 10, lg: 16}} alignItems="center">
                    <Box>
                        <SectionHeading eyebrow="About us" title={site.name} align="left" mb={6}/>
                        <Text fontSize={{base: "md", md: "lg"}} color="ink.700" lineHeight={1.8}>
                            With an English and Australian background in carpentry and joinery, we have more than 20 years of
                            experience as experts in providing highly qualified and licensed carpentry and joinery services.
                        </Text>
                        <Text mt={4} color="ink.500" lineHeight={1.8}>
                            We offer competitive pricing with high quality products, a free estimate and quote, and professional
                            supply, installation and custom made carpentry and renovation services.
                        </Text>
                        <SimpleGrid columns={{base: 1, sm: 2}} spacing={3} mt={8}>
                            {points.map((point) => (
                                <HStack key={point} spacing={3} align="center">
                                    <Flex w="28px" h="28px" bg="brand.500" color="white" align="center" justify="center" flexShrink={0}>
                                        <CheckIcon boxSize={3}/>
                                    </Flex>
                                    <Text fontWeight={600} fontSize="sm">{point}</Text>
                                </HStack>
                            ))}
                        </SimpleGrid>
                    </Box>
                    <Box position="relative">
                        <Box position="absolute" top={{base: 3, md: 5}} left={{base: 3, md: 5}} right={{base: -3, md: -5}}
                             bottom={{base: -3, md: -5}} border="3px solid" borderColor="brand.500"/>
                        <AspectRatio ratio={16 / 10} position="relative" bg="ink.900" boxShadow="xl">
                            <video src={`${process.env.PUBLIC_URL}/videos/coverpage.mp4`} controls autoPlay muted loop playsInline
                                   preload="metadata" style={{objectFit: "cover"}}/>
                        </AspectRatio>
                    </Box>
                </SimpleGrid>
            </Container>
        </Box>
    );
}

function Services() {
    return (
        <Box as="section" id="services" py={{base: 16, md: 24}}>
            <Container maxW="container.xl">
                <SectionHeading eyebrow="What we do" title="Qualified & licensed carpentry services">
                    From second fix carpentry to bespoke joinery, our team handles the full job from supply to installation.
                </SectionHeading>
                <SimpleGrid columns={{base: 1, md: 2, lg: 3}} spacingX={10}>
                    {services.map((service) => (
                        <HStack key={service} spacing={3} py={3.5} borderBottom="1px solid" borderColor="ink.100" align="start">
                            <CheckIcon color="brand.500" boxSize={3.5} mt={1.5} flexShrink={0}/>
                            <Text color="ink.700">{service}</Text>
                        </HStack>
                    ))}
                </SimpleGrid>
            </Container>
        </Box>
    );
}

function Process() {
    const {openQuote} = useQuote();
    return (
        <Box as="section" id="process" position="relative" py={{base: 16, md: 24}} bg="ink.900" overflow="hidden">
            <Image src={processBg} alt="" position="absolute" inset={0} w="100%" h="100%" objectFit="cover" opacity={0.18}/>
            <Container maxW="container.xl" position="relative">
                <SectionHeading eyebrow="Our seamless process" title="How it works" light/>
                <Box position="relative">
                    <Box display={{base: "none", lg: "block"}} position="absolute" top="28px" left="10%" right="10%" h="1px" bg="whiteAlpha.300"/>
                    <SimpleGrid columns={{base: 1, md: 2, lg: 5}} spacing={{base: 10, lg: 8}} position="relative">
                        {steps.map((step, i) => (
                            <Box key={step.title} textAlign={{base: "left", lg: "center"}}>
                                <Flex w="56px" h="56px" mx={{base: 0, lg: "auto"}} align="center" justify="center" borderRadius="full"
                                      bg="ink.900" border="2px solid" borderColor="brand.500" color="white" fontFamily="heading"
                                      fontWeight={700} fontSize="xl">
                                    {String(i + 1).padStart(2, "0")}
                                </Flex>
                                <Heading as="h3" color="white" fontSize="lg" textTransform="uppercase" letterSpacing="0.06em" mt={5}>
                                    {step.title}
                                </Heading>
                                <Text mt={3} fontSize="sm" color="whiteAlpha.700" lineHeight={1.7}>{step.description}</Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Box>
                <Flex justify="center" mt={14}>
                    <Button size="lg" onClick={() => openQuote()}>Request a free quote</Button>
                </Flex>
            </Container>
        </Box>
    );
}

function RecentProjects() {
    const [open, setOpen] = useState(null);
    const items = useMemo(() => featuredPhotos
        .map((file) => catalogPhotos.find((photo) => photo.file === file))
        .filter(Boolean)
        .map(({file, category, sub}) => ({
            type: "image",
            src: imageUrl(file),
            title: sub.description,
            subtitle: category.title,
            href: subCategoryUrl(category, sub),
        })), []);

    return (
        <Box as="section" py={{base: 16, md: 24}}>
            <Container maxW="container.xl">
                <SectionHeading eyebrow="Gallery" title="Recent projects"/>
                <SimpleGrid columns={{base: 2, md: 4}} spacing={{base: 2, md: 4}}>
                    {items.map((item, i) => (
                        <AspectRatio key={item.src} ratio={1}>
                            <Box as="button" type="button" data-group overflow="hidden" position="relative" bg="ink.100"
                                 onClick={() => setOpen(i)} aria-label={`Enlarge: ${item.title}`}>
                                <Image src={item.src} alt={item.title} loading="lazy" w="100%" h="100%" objectFit="cover"
                                       transition="transform 0.6s ease" _groupHover={{transform: "scale(1.06)"}}/>
                                <Box position="absolute" inset={0} bg="blackAlpha.600" opacity={0} _groupHover={{opacity: 1}}
                                     transition="opacity 0.3s" display="flex" alignItems="flex-end" p={4}>
                                    <Text color="white" fontSize="sm" fontWeight={600} textAlign="left" noOfLines={2}>{item.title}</Text>
                                </Box>
                            </Box>
                        </AspectRatio>
                    ))}
                </SimpleGrid>
                <Flex justify="center" mt={10}>
                    <Button as={RouterLink} to="/gallery" variant="outline" colorScheme="gray" borderColor="ink.800" color="ink.900"
                            size="lg" rightIcon={<ArrowForwardIcon/>} _hover={{bg: "ink.900", color: "white"}}>
                        View full gallery
                    </Button>
                </Flex>
            </Container>
            <Lightbox items={items} index={open} onClose={() => setOpen(null)} onChange={setOpen}/>
        </Box>
    );
}

function Contact() {
    return (
        <Box as="section" id="contact" py={{base: 16, md: 24}} bg="ink.50">
            <Container maxW="container.xl">
                <SimpleGrid columns={{base: 1, lg: 5}} boxShadow="0 20px 50px rgba(0,0,0,0.08)">
                    <Box gridColumn={{lg: "span 2"}} bg="ink.900" color="whiteAlpha.800" p={{base: 8, md: 12}}>
                        <SectionHeading eyebrow="Get in touch" title="Book a free estimate & quote" align="left" light mb={6}/>
                        <Text lineHeight={1.8}>
                            Tell us about your project and we will get back to you to arrange a time to measure and quote.
                            Please fill in every required field so your request reaches us.
                        </Text>
                        <VStack align="start" spacing={4} mt={10}>
                            <Link href={site.phoneHref} display="flex" alignItems="center" gap={4} color="white"
                                  _hover={{color: "brand.200", textDecoration: "none"}}>
                                <Flex w="44px" h="44px" bg="brand.500" align="center" justify="center"><PhoneIcon/></Flex>
                                <Box>
                                    <Text fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="whiteAlpha.600">Call us</Text>
                                    <Text fontWeight={700} fontSize="lg">{site.phone}</Text>
                                </Box>
                            </Link>
                            <Link href={`mailto:${site.email}`} display="flex" alignItems="center" gap={4} color="white"
                                  _hover={{color: "brand.200", textDecoration: "none"}}>
                                <Flex w="44px" h="44px" bg="brand.500" align="center" justify="center" flexShrink={0}><EmailIcon/></Flex>
                                <Box minW={0}>
                                    <Text fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="whiteAlpha.600">Email us</Text>
                                    <Text fontWeight={600} wordBreak="break-all">{site.email}</Text>
                                </Box>
                            </Link>
                        </VStack>
                        <Text mt={10} fontSize="sm" color="whiteAlpha.600">
                            ABN {site.abn} · Licence No. {site.license}
                        </Text>
                    </Box>
                    <Box gridColumn={{lg: "span 3"}} bg="white" p={{base: 6, md: 12}}>
                        <QuoteForm/>
                    </Box>
                </SimpleGrid>
            </Container>
        </Box>
    );
}

function Home() {
    usePageTitle();
    return (
        <>
            <Hero/>
            <Reasons/>
            <Products/>
            <About/>
            <Services/>
            <Process/>
            <RecentProjects/>
            <Contact/>
        </>
    );
}

export default Home;
